import { z } from 'zod';
import { condition, type FieldCondition } from './filter-ast';

/**
 * "MARK AS REVIEWED" ON AN INTAKE (redesign register D10: "reviewed at and by, separate from
 * the importer's Needs review"): `POST` and `DELETE /api/intake/submissions/:submissionId/reviewed`.
 *
 * A person has read what the client wrote before the first call, so nobody reads it twice. The
 * time lives on the platform's deliveries object (`intake/object.ts`, field `reviewed_at`,
 * "Reviewed at"), because "a person reviewed this delivery" says nothing about OFMOS; the
 * importer's own "Needs review" status is a different fact and is left alone.
 *
 * THE SERVER'S CLOCK, NOT THE BROWSER'S, for the reason `reply-handled.ts` gives: `POST`
 * stamps the server's now and takes no body, `DELETE` empties the field again, and both answer
 * the value the record holds afterwards. WHO pressed the button is the actor of that write,
 * which the record's timeline carries (INV-3).
 */
export const intakeReviewedSchema = z.strictObject({
  submissionId: z.uuid(),
  /** ISO 8601 with an offset, or null when nobody has reviewed the delivery. */
  reviewedAt: z.iso.datetime({ offset: true }).nullable(),
});

export type IntakeReviewed = z.infer<typeof intakeReviewedSchema>;

/** The field's api name on the deliveries object, which the browser finds it by. */
export const INTAKE_REVIEWED_AT_FIELD = 'reviewed_at';

/**
 * THE DELIVERIES NOBODY HAS REVIEWED YET, as the filter `POST /api/query` takes: "Reviewed at"
 * is empty. `reviewedAtFieldId` is the field's id on this installation (`fieldByName(objectId,
 * INTAKE_REVIEWED_AT_FIELD)`); the condition is the same one both filter backends evaluate
 * (INV-6), so a list built from it and a count built from it cannot disagree.
 */
export function notYetReviewedFilter(reviewedAtFieldId: string): FieldCondition {
  return condition(reviewedAtFieldId, 'isEmpty');
}
